import {
  type ChangeEvent,
  type ChangeEventHandler,
  useMemo,
  useState,
} from "react";
import { type CountryCode } from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";

import { phoneCodesOptions } from "@vega-api/custom/services";
import { useGetCountryList } from "@vega-api/generated/country-v1/country-v1";
import { Dropdown } from "@vega-core/components/Form/Controls/Dropdown/Dropdown/Dropdown";
import { InputControl } from "@vega-core/components/Form/Controls/Input/InputControl";
import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { DEFAULT_PHONE_CODE } from "@vega-core/constants/global";
import type { OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

type Props = {
  hideLabel?: boolean;
  background?: string;
  errorsPhoneNumber?: string;
  handleBlur?: (e: unknown) => void;
  handleChange?: ChangeEventHandler<Element>;
  isOptional?: boolean;
  name?: string;
  label?: string;
  phoneCode?: string;
  phoneNumber?: number | string | null;
  setFieldValue: (key: string, value: string) => void;
  touchedPhoneNumber?: boolean;
  canCopyToAllPhones?: boolean;
  onCopyAllPhones?: (phone: string) => void;
  fullWidth?: boolean;
  constrainInputWidth?: boolean;
};

const getByPlaceholderText = (phoneCode: CountryCode) => examples[phoneCode];

export const PhoneInput = ({
  background,
  errorsPhoneNumber,
  handleBlur,
  handleChange,
  hideLabel,
  label,
  isOptional,
  name = "phoneNumber",
  phoneCode,
  phoneNumber,
  setFieldValue,
  touchedPhoneNumber,
  canCopyToAllPhones,
  onCopyAllPhones,
  fullWidth,
  constrainInputWidth = false,
}: Props) => {
  const [regionCode, setRegionCode] = useState<CountryCode | undefined>("US");
  const { data: { data: countriesList = [] } = {} } = useGetCountryList();

  const phoneCodes = useMemo<OptionType[]>(() => {
    if (isFalsy(countriesList) || countriesList.length === 0) {
      return [DEFAULT_PHONE_CODE] as OptionType[];
    }

    return phoneCodesOptions(countriesList) satisfies OptionType[];
  }, [countriesList]);

  const phoneCodeValue = useMemo(
    // () => (countriesFetched ? phoneCode : undefined), // todo remove temporary fix
    () => phoneCode,
    [phoneCode],
  );

  const phoneNumberPlaceholder = regionCode && getByPlaceholderText(regionCode);

  const handleDropDownChange = (val: OptionType) => {
    setFieldValue("phoneCode", val.value);
    val.regionCode && setRegionCode(val.regionCode);
  };

  const showCopyToAllPhones = useMemo(
    () => canCopyToAllPhones && isTruthy(phoneCode) && isTruthy(phoneNumber),
    [phoneCode, phoneNumber],
  );

  return (
    <div className={cnTwMerge("h-full", fullWidth && "w-full")}>
      {!hideLabel && (
        <div
          className={cnTwMerge("flex justify-between", fullWidth && "w-full")}
        >
          <FormLabel
            isOptional={isOptional}
            label={label ?? m.formFields_phoneNumber()}
          />
          {canCopyToAllPhones && showCopyToAllPhones ? (
            <p
              className="text-text/interactive shrink-0 block cursor-pointer body-small-medium"
              onClick={() =>
                onCopyAllPhones &&
                onCopyAllPhones(`${phoneCodeValue}${phoneNumber}`)
              }
            >
              {m.formFields_copyToAll()}
            </p>
          ) : null}
        </div>
      )}

      <div className={cnTwMerge("flex", constrainInputWidth && "max-w-[50%]")}>
        <div className="mr-2 w-28">
          <Dropdown
            isSearchable
            name="phoneCode"
            onChange={handleDropDownChange}
            options={phoneCodes}
            placeholder="+1"
            value={phoneCodeValue}
          />
        </div>
        <div className="flex-1">
          <InputControl
            aria-label={m.formFields_phoneNumber()}
            background={background}
            error={
              errorsPhoneNumber && touchedPhoneNumber ? errorsPhoneNumber : ""
            }
            name={handleChange ? name : undefined}
            onBlur={handleBlur}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              handleChange
                ? handleChange(event)
                : setFieldValue("phoneNumber", event.target.value);
            }}
            placeholder={phoneNumberPlaceholder || m.placeholders_phoneNumber()}
            type="number"
            value={phoneNumber ?? ""}
            withBorder
          />
        </div>
      </div>
    </div>
  );
};
